import React from 'react';
import PropTypes from 'prop-types';
import ExtendedAvatar from 'components/extendedAvatar/extended-avatar';
import { Popover } from 'antd';
import { getActiveIcon } from 'library/helpers';
import { THEME_NAMES } from 'selectors/tag.selectors';
import { ItemTags } from 'views/shared/ItemTags';
import { UserCompetencies } from 'views/shared/UserCompetencies';
import { UserCycle } from 'views/availability/UserCycle';
import { Leave } from 'views/availability/Leave';
import { UserPermissions, UserProfile, UserTravel } from 'views/Users';
import { ICal } from 'views/shared';
import { CanViewAvailability, CanViewUsers } from 'library/permissions';
import AccountInput from './input';

const UserActiveHover = ({ children, userActive, tenancyActive }) => {
  const content = (
    <div>
      {getActiveIcon(userActive)}
      {' '}
      User account active
      <br />
      {getActiveIcon(tenancyActive)}
      {' '}
      Tenancy access
    </div>
  );
  return (
    <Popover content={content}>
      {children}
    </Popover>
  );
};

UserActiveHover.propTypes = {
  children: PropTypes.any.isRequired,
  userActive: PropTypes.bool.isRequired,
  tenancyActive: PropTypes.bool.isRequired
};

const config = {
  // List Table
  list: {
    // Table Columns
    commands: {
      showDelete: false
    },
    columns: [
      {
        title: '',
        dataIndex: 'id',
        width: '56px',
        align: 'right',
        render: (text, record) => (
          <ExtendedAvatar name={`${record.first_name} ${record.last_name}`} size="small" />
        )
      },
      {
        title: 'First Name',
        dataIndex: 'first_name',
        width: '180px',
        sorter: true
      },
      {
        title: 'Last Name',
        dataIndex: 'last_name',
        width: '180px',
        sorter: true,
        defaultSortOrder: 'ascend'
      },
      {
        title: 'Email',
        dataIndex: 'email',
        sorter: true
      },
      {
        title: 'AD User',
        dataIndex: 'is_ad_user',
        width: '130px',
        align: 'center',
        sorter: true,
        filterMultiple: false,
        filters: [{
          text: 'AD User',
          value: true
        }, {
          text: 'Non-AD User',
          value: false
        }],
        render: (text, record) => getActiveIcon(record.is_ad_user)
      },
      {
        title: 'Active',
        dataIndex: 'is_active',
        width: '120px',
        align: 'center',
        sorter: true,
        filterMultiple: false,
        filters: [{
          text: 'Active',
          value: true
        }, {
          text: 'Inactive',
          value: false
        }],
        render: (text, record) => (
          <UserActiveHover userActive={record.is_active} tenancyActive={record.is_tenancy_active}>
            {getActiveIcon(record.is_active && record.is_tenancy_active)}
          </UserActiveHover>
        )
      }
    ],
    // Deleting
    confirmMessage: item => (
      <div>
        <p>
          Are you sure you wish to delete the user
          <b>
            {item.first_name}
            {' '}
            {item.last_name}
          </b>
          ?
        </p>
        <p>
          This action cannot be undone.
        </p>
      </div>
    ),
    // Filtering
    sorter: {
      field: 'last_name',
      order: 'ascend'
    },
    filters: {},
    customFilters: {
      first_name: {
        filterDropdownVisible: false,
        filtered: false,
        contains: true,
        searchFields: ['first_name'],
        searchValue: ''
      },
      last_name: {
        filterDropdownVisible: false,
        filtered: false,
        contains: true,
        searchFields: ['last_name'],
        searchValue: ''
      },
      email: {
        filterDropdownVisible: false,
        filtered: false,
        contains: true,
        searchFields: ['email'],
        searchValue: ''
      }
    },
    // General
    title: 'Users',
    showFilter: true
  },
  // Detail Screen
  detail: {
    // General
    emptyMessage: 'Please select a User',
    title: 'User',
    tabItems: [
      {
        key: 0,
        title: 'Account',
        component: props => (
          <AccountInput
            {...props}
            title="User"
          />
        ),
        show: () => true
      },
      {
        key: 1,
        title: 'Profile',
        component: props => (
          <UserProfile
            {...props}
            title="Profile"
          />
        ),
        show: () => true
      },
      {
        key: 2,
        title: 'Roster',
        component: props => (
          <ICal
            {...props}
            title="Profile"
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      },
      {
        key: 3,
        title: 'Tags',
        component: props => (
          <ItemTags
            {...props}
            title="Tags"
            themeName={THEME_NAMES.user}
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      },
      {
        key: 4,
        title: 'Availability',
        component: props => (
          <UserCycle
            {...props}
            title="Availability"
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      },
      {
        key: 5,
        title: 'Leave',
        component: props => (
          <Leave
            {...props}
            title="Leave"
          />
        ),
        show: () => CanViewAvailability()
          .hasPermissions()
      },
      {
        key: 6,
        title: 'Travel',
        component: props => (
          <UserTravel
            {...props}
            title="Travel"
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      },
      {
        key: 7,
        title: 'Competencies',
        component: props => (
          <UserCompetencies
            {...props}
            title="Competencies"
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      },
      {
        key: 8,
        title: 'Permissions',
        component: props => (
          <UserPermissions
            {...props}
            title="Permissions"
          />
        ),
        show: () => CanViewUsers()
          .hasPermissions()
      }
    ]
  }
};

export default config;
